import { IconButton } from "@package/subframe/components/IconButton";
import { Loader } from "@package/subframe/components/Loader";
import { FeatherArrowLeft } from "@subframe/core";
import {
  createFileRoute,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useState } from "react";
import { toast } from "sonner";

import { AcknowledgementRecipientPicker } from "@/components/event-records/AcknowledgementRecipientPicker";
import { parseFieldSchema } from "@/components/event-records/event-record-schema";
import { EventRecordForm } from "@/components/event-records/EventRecordForm";
import {
  DerivedTimingRows,
  deriveTimingRows,
  FIRE_DRILL_SLUG,
  RESPONSIBLE_PERSON_KEY,
  ResponsiblePersonSelect,
  TIME_STEP_SECONDS,
} from "@/components/event-records/fire-drill";
import { useAuth } from "@/contexts/AuthContext";
import { useNursery } from "@/contexts/NurseryContext";
import { useListEventTypesQueryControllerList } from "@/generated/event-types/event-types";
import { useListNurseryMembersQueryControllerListMembers } from "@/generated/members/members";
import { useOfflineEventRecords } from "@/hooks/use-offline-event-records";
import { canBeAskedToAcknowledge } from "@/lib/member-display";

type NewEventRecordSearch = {
  eventTypeId: string;
};

export const Route = createFileRoute("/_authenticated/event-records/new")({
  validateSearch: (search: Record<string, unknown>): NewEventRecordSearch => ({
    eventTypeId:
      typeof search.eventTypeId === "string" ? search.eventTypeId : "",
  }),
  component: ReportEventPage,
});

function ReportEventPage() {
  const { eventTypeId } = useSearch({
    from: "/_authenticated/event-records/new",
  });
  const navigate = useNavigate();
  const { selectedNurseryId } = useNursery();
  const { user } = useAuth();
  const { createEventRecord, isOffline } =
    useOfflineEventRecords(selectedNurseryId);

  const { data, isLoading } = useListEventTypesQueryControllerList(
    selectedNurseryId ?? "",
    { query: { enabled: !!selectedNurseryId } },
  );
  const eventType = data?.data?.items.find((t) => t.id === eventTypeId);
  const isFireDrill = eventType?.slug === FIRE_DRILL_SLUG;

  // The nursery's users, for fire drill's "Responsible person" dropdown and for
  // NAU-342's "request acknowledgement from" picker — so fetched for every event
  // type now, not only fire drill.
  const { data: membersData, isLoading: isMembersLoading } =
    useListNurseryMembersQueryControllerListMembers(
      { nurseryId: selectedNurseryId ?? "", limit: 100 },
      { query: { enabled: !!selectedNurseryId } },
    );
  const members = membersData?.data?.data ?? [];
  // Only people who can read the record may be asked to acknowledge it, and never
  // the filer themselves — both rules are enforced server-side too.
  const askableMembers = members.filter(
    (member) =>
      canBeAskedToAcknowledge(member.role) && member.userId !== user?.id,
  );

  // Non-schema state, so it lives here rather than in the form's zod schema.
  const [acknowledgementRequestUserIds, setAcknowledgementRequestUserIds] =
    useState<string[]>([]);
  const toggleRecipient = (userId: string) =>
    setAcknowledgementRequestUserIds((previous) =>
      previous.includes(userId)
        ? previous.filter((id) => id !== userId)
        : [...previous, userId],
    );

  // Events are reported from the Workspace's Events tab (NAU-442) — return
  // there after submit/cancel.
  const goBack = () =>
    navigate({ to: "/checklists", search: { tab: "events" } });

  const handleSubmit = async (formData: Record<string, unknown>) => {
    if (!eventType) return;
    try {
      await createEventRecord({
        eventTypeId: eventType.id,
        eventTypeName: eventType.name,
        data: formData,
        acknowledgementRequestUserIds:
          acknowledgementRequestUserIds.length > 0
            ? acknowledgementRequestUserIds
            : undefined,
      });
      toast.success(
        isOffline
          ? `${eventType.name} saved — it will sync when you're back online`
          : `${eventType.name} submitted`,
      );
      goBack();
    } catch (err: unknown) {
      // Surface the server's validation message (e.g. an invalid dropdown
      // value the client somehow let through) rather than a generic error.
      let message = "Failed to submit record — please try again";
      if (
        err &&
        typeof err === "object" &&
        "response" in err &&
        err.response &&
        typeof err.response === "object" &&
        "data" in err.response
      ) {
        const body = err.response.data as Record<string, unknown>;
        if (typeof body.message === "string") message = body.message;
      }
      toast.error(message);
    }
  };

  return (
    <div className="flex w-full max-w-2xl flex-col items-start gap-6 bg-white animate-fade-in">
      <div className="flex w-full items-center gap-2">
        <IconButton icon={<FeatherArrowLeft />} onClick={goBack} />
        <div className="flex grow flex-col items-start">
          <span className="text-metrics font-metrics text-primary-navy-800">
            {eventType?.name ?? "Report an event"}
          </span>
          {eventType?.description && (
            <span className="text-body font-body text-subtext-color">
              {eventType.description}
            </span>
          )}
        </div>
      </div>

      {isLoading ? (
        <div className="flex w-full items-center justify-center py-12">
          <Loader size="large" />
        </div>
      ) : !eventType ? (
        <div className="flex w-full flex-col items-start gap-2 rounded-md border border-neutral-200 p-6">
          <span className="text-body font-body text-default-font">
            This event type isn&apos;t available. Go back and pick one from the
            list.
          </span>
        </div>
      ) : (
        (() => {
          const schema = parseFieldSchema(eventType.fieldSchema);
          return (
            <EventRecordForm
              schema={schema}
              onSubmit={handleSubmit}
              onCancel={goBack}
              timeStep={isFireDrill ? TIME_STEP_SECONDS : undefined}
              fieldRenderers={
                isFireDrill
                  ? {
                      [RESPONSIBLE_PERSON_KEY]: (props) => (
                        <ResponsiblePersonSelect
                          {...props}
                          members={members}
                          isLoading={isMembersLoading}
                        />
                      ),
                    }
                  : undefined
              }
              renderDerived={
                isFireDrill
                  ? (formData) => (
                      <DerivedTimingRows
                        rows={deriveTimingRows(schema, formData)}
                      />
                    )
                  : undefined
              }
              renderExtra={() => (
                <section className="flex w-full flex-col items-start gap-2 rounded-md border border-solid border-neutral-border p-4">
                  <span className="text-heading-3 font-heading-3 text-default-font">
                    Request acknowledgement (optional)
                  </span>
                  <span className="text-caption font-caption text-subtext-color">
                    Ask a manager or deputy manager to confirm they have read
                    this. Their reply is recorded on the record but does not
                    resolve it.
                  </span>
                  <AcknowledgementRecipientPicker
                    members={askableMembers}
                    selectedUserIds={acknowledgementRequestUserIds}
                    onToggle={toggleRecipient}
                    isLoading={isMembersLoading}
                  />
                </section>
              )}
            />
          );
        })()
      )}
    </div>
  );
}
